import { useState, useEffect, useRef } from 'react';
import type { PretextLayoutResult } from '../../lib/pretext/types';
import { buildFontString } from '../../lib/pretext/fonts';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface WinnerRationaleProps {
  winnerPhotoUrl?: string;
  winnerScore: number;
  rationaleTitle: string; // e.g. "Lead photo recommendation"
  rationaleText: string; // e.g. "This photo wins because..."
  traitComparisons: { trait: string; winner: number; loser: number }[];
  vertical: 'dating' | 'linkedin';
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function accentClasses(vertical: 'dating' | 'linkedin') {
  return vertical === 'dating'
    ? {
        border: 'border-rose/20',
        bg: 'bg-rose-faint',
        text: 'text-rose',
        pill: 'border-rose/20 bg-rose-faint text-rose',
        score: 'text-rose',
      }
    : {
        border: 'border-blue-200',
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        pill: 'border-blue-200 bg-blue-50 text-blue-700',
        score: 'text-blue-600',
      };
}

function scoreColorClass(score: number): string {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-rose';
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function WinnerRationale({
  winnerPhotoUrl,
  winnerScore,
  rationaleTitle,
  rationaleText,
  traitComparisons,
  vertical,
  className = '',
}: WinnerRationaleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<PretextLayoutResult | null>(null);
  const accent = accentClasses(vertical);

  // Pretext measurement for editorial text wrapping around photo obstacle
  useEffect(() => {
    if (!containerRef.current || !winnerPhotoUrl) return;

    let cancelled = false;

    const measure = async () => {
      try {
        const { measureTextWithLines } = await import(
          '../../lib/pretext/measure'
        );
        const font = buildFontString(400, 16, 'ui');
        const width = containerRef.current?.offsetWidth ?? 500;
        const lineHeightPx = 16 * 1.6;

        const result = await measureTextWithLines(
          rationaleText,
          font,
          width,
          lineHeightPx,
        );
        if (!cancelled && result) setLayout(result);
      } catch {
        // Pretext unavailable -- CSS fallback handles it
      }
    };

    measure();
    return () => {
      cancelled = true;
    };
  }, [rationaleText, winnerPhotoUrl]);

  const visibleComparisons = traitComparisons.slice(0, 3);

  return (
    <section
      className={`winner-rationale ${className}`}
      data-testid="winner-rationale"
      data-vertical={vertical}
    >
      <div
        ref={containerRef}
        className={`rounded-xl border ${accent.border} bg-surface p-5 sm:p-6`}
      >
        {/* Title row with score badge */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3
            className="font-serif text-xl text-ink"
            style={{ fontWeight: 600 }}
            data-testid="winner-rationale-title"
          >
            {rationaleTitle}
          </h3>
          <span
            className={`font-sans text-2xl ${scoreColorClass(winnerScore)}`}
            style={{ fontWeight: 600 }}
            data-testid="winner-rationale-score"
          >
            {winnerScore}
          </span>
        </div>

        {/* Editorial flow: photo floats left, text wraps around */}
        <div className="editorial-flow">
          {winnerPhotoUrl && (
            <div className="float-left mr-5 mb-3 w-28 sm:w-36">
              <img
                src={winnerPhotoUrl}
                alt="Winner photo"
                className={`w-full rounded-lg border ${accent.border} object-cover aspect-[3/4]`}
              />
            </div>
          )}

          <div
            className="font-sans text-sm text-ink-muted leading-relaxed"
            style={{ fontWeight: 300 }}
            data-testid="winner-rationale-text"
            data-pretext-lines={layout?.lineCount}
          >
            {layout?.lines ? (
              layout.lines.map((line, i) => (
                <span key={i} className="block">
                  {line.text}
                </span>
              ))
            ) : (
              <p>{rationaleText}</p>
            )}
          </div>

          {/* Clear float */}
          <div className="clear-both" />
        </div>

        {/* Trait comparison tags */}
        {visibleComparisons.length > 0 && (
          <div
            className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-surface-border"
            data-testid="winner-rationale-comparisons"
          >
            {visibleComparisons.map((comp) => (
              <span
                key={comp.trait}
                className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-sans whitespace-nowrap ${accent.pill}`}
              >
                <span className="capitalize">{comp.trait}</span>
                <span className="mx-1.5 text-ink-faint">:</span>
                <span style={{ fontWeight: 600 }}>{comp.winner.toFixed(1)}</span>
                <span className="mx-1 text-ink-faint">vs</span>
                <span className="text-ink-faint">{comp.loser.toFixed(1)}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
